import * as Placeholder from '-/Custom/Placeholder';
import { Link, useLocalSearchParams } from 'expo-router';
import { useQuery, useSubscription } from 'urql';
import FadeInContent from '@/components/Animation/FadeInContent';
import EventBanner, {
  EventBannerFragment,
} from '@/components/Event/EventBanner';
import * as EventDetailInfo from '@/components/Event/EventDetailInfo';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import Card from '@/components/UI/Card';
import { graphql, readFragment } from '@/gql/graphql';
import useActiveTimestamp from '@/hooks/useActiveTimestamp';
import useUserId from '@/hooks/useUserId';
import EventDetailBannerInfo, {
  EventDetailBannerInfoFragment,
} from '@/layouts/EventDetail/EventDetailBannerInfo';
import { EVENT_TEAM_LIMIT } from '@/utils/constants';
import isEventStarted from '@/utils/isEventStarted';
import EventDetailHomeTeams, {
  type EventDetailHomePathname,
  EventDetailHomeRosterFragment,
  EventDetailHomeRosterPriorityFragment,
  EventDetailHomeTeamsFragment,
  type EventTeamMemberPathname,
} from './components/EventDetailHomeTeams';

const EventDetailHomeFragment = graphql(/* GraphQL */ `
  fragment EventDetailHome on event {
    event_type
    title
    permissions
    event {
      id
    }
    datetime
    location
    notes
  }
`);
const GetEventDetailHome = graphql(/* GraphQL */ `
  query GetEventDetailHome(
    $eventId: uuid!
    $personId: String!
    $teamLimit: Int!
  ) {
    event_by_pk(id: $eventId) {
      id
      ...EventDetailHome
      ...EventDetailInfoDatetime
      ...EventDetailInfoNotes
      ...EventDetailInfoLocation
      ...EventBanner
      ...EventDetailBannerInfo
      ...EventDetailHomeTeams
    }
    ...EventDetailHomeRosterPriority
    ...EventDetailHomeRoster
  }
`, [
  EventDetailHomeFragment,
  EventDetailInfo.DatetimeFragment,
  EventDetailInfo.NotesFragment,
  EventDetailInfo.LocationFragment,
  EventBannerFragment,
  EventDetailBannerInfoFragment,
  EventDetailHomeTeamsFragment,
  EventDetailHomeRosterPriorityFragment,
  EventDetailHomeRosterFragment,
]);
const WatchEventDetailHome = graphql(/* GraphQL */ `
  subscription WatchEventDetailHome($eventId: uuid!, $teamLimit: Int!) {
    event_by_pk(id: $eventId) {
      id
      ...EventDetailHome
      ...EventDetailInfoDatetime
      ...EventDetailInfoNotes
      ...EventDetailInfoLocation
      ...EventBanner
      ...EventDetailBannerInfo
      ...EventDetailHomeTeams
    }
  }
`, [
  EventDetailHomeFragment,
  EventDetailInfo.DatetimeFragment,
  EventDetailInfo.NotesFragment,
  EventDetailInfo.LocationFragment,
  EventBannerFragment,
  EventDetailBannerInfoFragment,
  EventDetailHomeTeamsFragment,
]);
export const WatchEventDetailRsvpStream = graphql(/* GraphQL */ `
  subscription WatchEventDetailRsvpStream(
    $cursor: timestamptz!
    $eventId: uuid!
  ) {
    rsvp_stream(
      batch_size: 100
      cursor: { initial_value: { updated_at: $cursor } }
      where: { event_id: { _eq: $eventId } }
    ) {
      event_id
      person_id
      notes
      status
      updated_at
    }
  }
`);
type EventDetailHomeProps = {
  basePath: EventDetailHomePathname;
  teamMemberPathname: EventTeamMemberPathname;
};
function EventDetailHome({
  basePath,
  teamMemberPathname,
}: EventDetailHomeProps) {
  const personId = useUserId();
  const rsvpCursor = useActiveTimestamp();
  const { eventId } = useLocalSearchParams<{
    eventId?: string;
  }>();
  const [{ data, fetching }] = useQuery({
    query: GetEventDetailHome,
    variables: {
      eventId: eventId || '',
      personId,
      teamLimit: EVENT_TEAM_LIMIT,
    },
    pause: !eventId,
  });
  useSubscription({
    query: WatchEventDetailHome,
    variables: {
      eventId: eventId || '',
      teamLimit: EVENT_TEAM_LIMIT,
    },
    pause: !eventId,
  });
  useSubscription({
    query: WatchEventDetailRsvpStream,
    variables: {
      cursor: rsvpCursor!,
      eventId: eventId || '',
    },
    pause: !eventId || !rsvpCursor,
  });
  const event = readFragment(EventDetailHomeFragment, data?.event_by_pk);
  const hasInfo =
    event?.event_type === 'game' ||
    event?.event ||
    event?.datetime ||
    event?.location ||
    event?.notes;
  const hasHome = hasInfo || event?.event_type === 'game';
  const hasEventBanner = event?.event_type === 'game';
  const hasEventInfo = !!event?.datetime || !!event?.location || !!event?.notes;
  const listHeader =
    hasEventBanner || hasEventInfo ? (
      <Box mx="md" my="sm" space="sm">
        {hasEventBanner ? (
          <Card background="surface" testID="EventBannerCard">
            <EventBanner
              hideScore={!isEventStarted(event.datetime)}
              info={
                data?.event_by_pk ? (
                  <EventDetailBannerInfo fragment={data.event_by_pk} />
                ) : null
              }
              fragment={data?.event_by_pk}
            />
          </Card>
        ) : null}
        {hasEventInfo ? (
          <Card background="surface" testID="EventInfoCard">
            {event?.datetime ? (
              <Box testID="EventDateTime">
                <EventDetailInfo.Datetime fragment={data?.event_by_pk} />
              </Box>
            ) : null}
            {event?.location ? (
              <EventDetailInfo.Location fragment={data?.event_by_pk} />
            ) : null}
            {event?.notes ? (
              <Link
                href={{
                  pathname: `${basePath}/notes`,
                  params: {
                    eventId,
                  },
                }}
                asChild
              >
                <EventDetailInfo.Notes fragment={data?.event_by_pk} />
              </Link>
            ) : null}
          </Card>
        ) : null}
      </Box>
    ) : null;

  return (
    <FadeInContent fetching={fetching}>
      {hasHome ? (
        <EventDetailHomeTeams
          key={eventId}
          listHeader={listHeader}
          priorityQuery={data}
          query={event?.event_type === 'game' ? data?.event_by_pk : undefined}
          rosterQuery={data}
          teamMemberPathname={teamMemberPathname}
        />
      ) : (
        <Placeholder.Container>
          <Placeholder.Icon name="globe-outline" />
          <Placeholder.Text>Welcome to {event?.title}.</Placeholder.Text>
          {event?.permissions?.create_event ? (
            <Link
              href={{
                pathname: '/event/create',
                params: {
                  parentId: eventId,
                  eventType: 'group',
                },
              }}
              asChild
            >
              <ActionButton
                label="Create League"
                icon="add-outline"
                variant="success"
              />
            </Link>
          ) : null}
          <Link
            href={{
              pathname: `${basePath}/schedules`,
              params: {
                eventId,
              },
            }}
            asChild
          >
            <ActionButton
              label="View Schedule"
              icon="calendar-outline"
              variant="secondary"
            />
          </Link>
        </Placeholder.Container>
      )}
    </FadeInContent>
  );
}
export default EventDetailHome;
